import { formatNumber } from "@/v2-common/utils/utils.formatting"
import { DATA_VIZ } from "@/v2-ui/color/dataViz.colors"
import type { ColumnFilterOption } from "@/v2-backoffice-client/components/table"

/**
 * Labels for the curated app-area codes produced by the Databricks gold
 * feature-area mapping (main.tracking_gold.feature_area_map, versioned in
 * apps/etl/v2-usage-sync/databricks/gold.refresh.sql). The codes mirror the
 * commercial use cases sales/CS talk about (placepoint.no "bruksområder") so
 * a user's top areas read as a customer profile. Unknown codes fall back to
 * the raw code so a mapping addition never renders blank.
 */
export const APP_AREA_LABELS: Record<string, string> = {
  property_research: "Property research",
  company_research: "Company research",
  owners_stakeholders: "Owners & stakeholders",
  tenants_leasing: "Tenants & leasing",
  transactions_value: "Transactions & value",
  regulation_development: "Regulation & development",
  climate_esg_risk: "Climate, ESG & risk",
  site_analysis_3d: "Site analysis & 3D",
  market_analytics: "Market analytics",
  portfolio_pipeline: "Portfolio & pipeline",
  search_navigation: "Search & navigation",
  account: "Account",
  support: "Support",
  other: "Other",
}

export function formatAppArea(area: string) {
  return APP_AREA_LABELS[area] ?? area
}

/**
 * The products usage can come from: the two console generations and the two
 * MCP servers (AIP-47). Customers buy them as separate products, so the
 * split is a first-class sales/CS signal (v2-upsell openings, MCP adoption,
 * per-product churn), not a technical detail. Codes match the gold-layer
 * appCode values, which derive them from the feature-code family
 * (`UI_*` → v2-console, `MCP_Placepoint_*` → mcp-placepoint, …).
 */
export const APP_CODE_LABELS: Record<string, string> = {
  "v1-console": "Console v1",
  "v2-console": "Console v2",
  "mcp-placepoint": "Placepoint MCP",
  "mcp-dataset": "Dataset MCP",
}

/** The MCP servers' app codes — the products the MCP usage section covers. */
const MCP_APP_CODES = ["mcp-placepoint", "mcp-dataset"] as const

export function isMcpAppCode(appCode: string) {
  return (MCP_APP_CODES as readonly string[]).includes(appCode)
}

/** The consoles' app codes — the products the Fusion tab covers. */
const CONSOLE_APP_CODES = ["v1-console", "v2-console"] as const

export function isConsoleAppCode(appCode: string) {
  return (CONSOLE_APP_CODES as readonly string[]).includes(appCode)
}

/** Every known app code, in display order (consoles first, then MCP). */
export const APP_CODES = Object.keys(APP_CODE_LABELS)

export function formatAppCode(appCode: string) {
  return APP_CODE_LABELS[appCode] ?? appCode
}

/**
 * Quick-filter options for "which product does this user actually live in".
 * Filtering is on the dominant app — the one with the most weighted activity
 * — so picking Console v1 answers "who hasn't moved over yet" and Placepoint
 * MCP "who works through an AI client first" in one click. Dots match the
 * split bar's colors so the filter reads like the column it filters.
 */
export const APP_CODE_FILTER_OPTIONS: ColumnFilterOption[] = [
  {
    value: "v2-console",
    label: APP_CODE_LABELS["v2-console"],
    dotVariant: "success-1",
  },
  {
    value: "v1-console",
    label: APP_CODE_LABELS["v1-console"],
    dotVariant: "warning",
  },
  {
    value: "mcp-placepoint",
    label: APP_CODE_LABELS["mcp-placepoint"],
    dotVariant: "info",
  },
  {
    value: "mcp-dataset",
    label: APP_CODE_LABELS["mcp-dataset"],
    dotVariant: "secondary",
  },
]

/**
 * Chart/split-bar color per app, from the sanctioned data-viz palette
 * (concrete values — Recharts fills and inline styles need them, and the
 * backoffice theme publishes no semantic success/warning vars): brand green
 * is the current-generation console, amber the legacy one, the series blue
 * the Placepoint MCP and the series purple the Dataset MCP. Deliberately NOT
 * the theme's `--primary` (amber chrome in the backoffice theme) so data
 * reads apart from UI chrome, and not the series teal either: that hue
 * already means "both consoles" in the Portfolio's mix funnel and "volume
 * trend" in the sparklines, and the MCP segment sits next to both.
 */
export const APP_CODE_COLORS: Record<string, string> = {
  "v1-console": DATA_VIZ.brand.amber,
  "v2-console": DATA_VIZ.brand.green2,
  "mcp-placepoint": DATA_VIZ.series[3],
  "mcp-dataset": DATA_VIZ.series[5],
}

export function getAppCodeColor(appCode: string) {
  return APP_CODE_COLORS[appCode] ?? DATA_VIZ.semantic.neutral
}

/** Plain-language explanation behind the product-mix sections. */
export const APP_MIX_TOOLTIP =
  "Which product the tracked activity comes from. Console v1, Console v2 " +
  "and the MCP servers are sold separately, so the split shows product fit: " +
  "v1-heavy accounts are v2 demo candidates, fading v1 usage without v2 " +
  "pickup is churn risk, and MCP activity is the AI-client seat count to sell."

/**
 * Trend buckets for TrendPct (28d vs the previous 28d). Null means the user
 * has no baseline window yet (new user or first sync cycles).
 */
export function getTrendLevel(trendPct: number | null | undefined) {
  if (trendPct == null) return "new" as const
  if (trendPct <= -50) return "falling-hard" as const
  if (trendPct <= -20) return "falling" as const
  if (trendPct >= 20) return "growing" as const
  return "steady" as const
}

export const TREND_LABELS: Record<ReturnType<typeof getTrendLevel>, string> = {
  "falling-hard": "Falling hard",
  falling: "Falling",
  steady: "Steady",
  growing: "Growing",
  new: "No baseline",
}

/** Plain-language explanation behind every trend pill. */
export const TREND_TOOLTIP =
  "Weighted activity in the last 28 days vs the 28 days before. " +
  "Weighted = heavy actions (exports, reports, saves) count more than clicks."

/**
 * What it takes for a usage signal to become a play. Tuned on the prod book
 * (767 customers, October 2026): below these floors the plays named almost
 * every account and the Playbook was a list of the base, not a week plan.
 * Volumes are weighted 28-day actions (exports, reports and saves count more
 * than clicks); "previous" is the 28 days before.
 */
export const PLAY_THRESHOLDS = {
  /** Idle seats: at least this many seats, this many idle, this idle share. */
  idleSeats: { minSeats: 5, minIdle: 3, minIdleShare: 0.5 },
  /**
   * Gone quiet: a person counts if they did this much in the previous
   * window; the play fires for at least this many people, or this share of
   * the seats (one of two is half the account).
   */
  newlyQuiet: { minPreviousWeighted: 20, minPeople: 2, minSeatShare: 1 / 3 },
  /**
   * Usage dropping: a person counts if they did this much before; the play
   * fires when the account itself is down this much, or the fallers are
   * this share of the active people.
   */
  falling: {
    minPreviousWeighted: 100,
    accountTrendPct: -30,
    minActiveShare: 1 / 3,
  },
  /**
   * Untouched use cases are an expansion angle only for an engaged account:
   * this many active people, this engagement, this many use cases already
   * in use, this much volume, and not at high churn risk.
   */
  untouched: { minActive: 2, minScore: 60, minUsedAreas: 3, minWeighted: 200 },
  /** Growth to anchor a renewal on needs a base in both windows. */
  champions: { minTrendPct: 20, minWeighted: 200, minPreviousWeighted: 100 },
  /** A reference-customer read needs breadth and steadiness. */
  steady: { minActive: 3, minScore: 60, maxIdleShare: 0.25, minWeighted: 200 },
  /** Console v2 demo: real Console v1 work by more than one person. */
  v1Only: { minWeighted: 300, minActiveSeats: 2 },
  /**
   * Console v1 volume below which a shift between consoles is noise: a
   * handful of clicks collapsing to one reads as -90 %.
   */
  consoleFloor: 50,
} as const

/** Above this the previous window was near zero and a percentage misleads. */
export const TREND_PCT_CEILING = 10_000

export function formatTrendPct(trendPct: number | null | undefined) {
  if (trendPct == null) return "—"
  if (trendPct >= TREND_PCT_CEILING) return "from ~0"
  const sign = trendPct > 0 ? "+" : ""
  return `${sign}${trendPct.toFixed(0)}%`
}

/**
 * The numbers behind a trend pill ("12 072 now vs 46 000 previous window"),
 * for hover titles — the pct alone hides magnitude, and magnitude is what
 * separates a churn signal from noise.
 */
export function formatTrendDetail(
  current: number | null | undefined,
  previous: number | null | undefined,
) {
  if (current == null || previous == null) return undefined
  return `${formatNumber(Math.round(current))} now vs ${formatNumber(Math.round(previous))} in the previous window`
}

/**
 * Engagement-score bands (0-100 composite computed in the Databricks gold
 * layer: weighted activity, breadth, recency, consistency). Null means the
 * row predates the scoring rollout or the user has no profile yet.
 */
export function getScoreLevel(score: number | null | undefined) {
  if (score == null) return "new" as const
  if (score >= 75) return "power" as const
  if (score >= 50) return "healthy" as const
  if (score >= 25) return "cooling" as const
  return "dormant" as const
}

export type ScoreLevel = ReturnType<typeof getScoreLevel>

export const SCORE_LEVEL_LABELS: Record<ScoreLevel, string> = {
  power: "Power user",
  healthy: "Healthy",
  cooling: "Cooling",
  dormant: "Dormant",
  new: "No score yet",
}

/** Canonical display order of the score levels, best first. */
export const SCORE_LEVEL_ORDER: ScoreLevel[] = [
  "power",
  "healthy",
  "cooling",
  "dormant",
  "new",
]

/** Plain-language explanation behind every score pill. */
export const SCORE_TOOLTIP =
  "Engagement 0–100, combining how much they do (weighted activity), " +
  "how broadly (feature areas), how recently, and how consistently. " +
  "75+ power user · 50–74 healthy · 25–49 cooling · below 25 dormant."

/**
 * Quick-filter options for the score-level toolbar filters (rendered by
 * Table.ToolbarFilters on the usage, teams, and customers boards). Score
 * levels are the four actionable bands — leaving everything unselected
 * keeps unscored rows visible too. The values are the level literals the
 * server-side ScoreLevel filter expression buckets by
 * (usagestats.filters.go), so the boards filter identically server- and
 * client-side.
 */
export const SCORE_LEVEL_FILTER_OPTIONS: ColumnFilterOption[] = (
  ["power", "healthy", "cooling", "dormant"] satisfies ScoreLevel[]
).map((level) => ({
  value: level,
  label: SCORE_LEVEL_LABELS[level],
  dotVariant: (
    {
      power: "success-1",
      healthy: "success-2",
      cooling: "warning",
      dormant: "destructive",
    } as const
  )[level],
}))

/**
 * Trend buckets with their thresholds spelled out — no guessing. Same
 * value/expression contract as SCORE_LEVEL_FILTER_OPTIONS (TrendLevel in
 * usagestats.filters.go).
 */
export const TREND_LEVEL_FILTER_OPTIONS: ColumnFilterOption[] = [
  {
    value: "growing",
    label: `${TREND_LABELS.growing} · +20% or more`,
    dotVariant: "success-1",
  },
  {
    value: "steady",
    label: `${TREND_LABELS.steady} · between −20% and +20%`,
    dotVariant: "secondary",
  },
  {
    value: "falling",
    label: `${TREND_LABELS.falling} · −20% to −50%`,
    dotVariant: "warning",
  },
  {
    value: "falling-hard",
    label: `${TREND_LABELS["falling-hard"]} · −50% or worse`,
    dotVariant: "destructive",
  },
]

/**
 * The dot in front of a play or finding, per tone: amber = act now, teal =
 * expansion opening, green = win to build on, grey = a plain fact. Shared
 * by the customer panel's findings and the Portfolio playbook, so the same
 * tone never renders in two colours.
 */
export const INSIGHT_TONE_DOT_CLASSES: Record<
  "action" | "opportunity" | "positive" | "neutral",
  string
> = {
  action: "bg-amber-400",
  opportunity: "bg-teal-400",
  positive: "bg-green-500",
  neutral: "bg-muted-foreground/50",
}

/**
 * Areas that describe how users move around the app, not what they care
 * about. Mirrors the isInterestArea exclusion in the Databricks gold layer
 * (gold.refresh.sql): they count toward volume but never toward interest
 * profiles or the "untouched areas" upsell list.
 */
export const HOUSEKEEPING_AREAS = new Set([
  "search_navigation",
  "account",
  "support",
  "other",
])

/** Interest areas (label order) — the sellable "bruksområder". */
export const INTEREST_AREAS = Object.keys(APP_AREA_LABELS).filter(
  (area) => !HOUSEKEEPING_AREAS.has(area),
)

/** Plain-language explanation behind the geographic-focus sections. */
export const GEO_AREAS_TOOLTIP =
  "Where their tracked work happens: every event carries the coordinates of " +
  "the property/company being viewed (or the map area in view), resolved to " +
  "municipalities. The share is of all location-carrying events, last 28 days."

/**
 * The definitions behind the console usage figures — the "About the
 * numbers" of the usage sections, built from the same texts the tooltips
 * show so a definition is written once.
 */
export const USAGE_REPORT_NOTES: { term: string; text: string }[] = [
  {
    term: "Activity",
    text:
      "Tracked console events (Console v1 and v2 alike), weighted by action: " +
      "exports, reports and saves count more than clicks. Computed daily " +
      "in the usage pipeline.",
  },
  { term: "Trend", text: TREND_TOOLTIP },
  { term: "Engagement", text: SCORE_TOOLTIP },
  {
    term: "Active seat",
    text:
      "A licensed user with at least one tracked event in the last 28 " +
      "days. Idle seats are paid for and unused — the first play on a " +
      "renewal.",
  },
  {
    term: "Churn risk",
    text:
      "Read from the seats, the trend and the recency together; the " +
      "findings list the signals behind it. Observations, not a verdict.",
  },
  { term: "Product mix", text: APP_MIX_TOOLTIP },
  {
    term: "Use case",
    text:
      "The commercial area a feature belongs to (property research, " +
      "owners & stakeholders, …), the vocabulary sales and CS use. " +
      "Navigation, account and support are left out of interest profiles.",
  },
  { term: "Where they work", text: GEO_AREAS_TOOLTIP },
]

/**
 * Municipality names arrive UPPERCASE from the registry ("NORD-FRON",
 * "VÅGAN"); render them as title case, respecting hyphens/spaces.
 */
export function formatMunicipalityName(name: string) {
  return name
    .toLowerCase()
    .replace(/(^|[\s-])(\p{L})/gu, (match) => match.toUpperCase())
}
